import React, {useContext, useEffect, useState} from "react";
import {CBContext} from "./CBContext";
import {
    Alert, Box, Button, CircularProgress, Collapse, Grid, List, ListItem, Paper, Tooltip, Typography
} from "@mui/material";
import {Formik} from "formik";
import TextField from "../utils/components/TextField";
import AutoCompleteField from "../utils/components/AutoCompleteField";
import RadioField from "../utils/components/RadioField";
import Mapping from "../utils/UtilityMapping.json";
import {computationValidationSchema, remittanceValidationSchema} from "./validations";
import FunctionsIcon from '@mui/icons-material/Functions';
import HelpIcon from '@mui/icons-material/Help';
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import API from "../utils/API";
import {ProgressContext} from "../utils/ProgressContext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import ComputationModal from "./ComputationModal";
import {useNavigate} from "react-router-dom";
import {generalAPI, makeNulls, removeNulls} from "./utils";
import {getPatchValues} from "../utils/utils";
import CancelIcon from "@mui/icons-material/Cancel";
import SaveIcon from "@mui/icons-material/Save";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {formData} from "./initialValues";
import APIError from "../utils/components/APIError";
import {formatAmount, getFigureWarnings} from "./figureWarnings";

const dtaaSections = [
    'A. If the Remittance is for royalties, fee for technical services, interests, dividend, etc (Not connected with permenant establishment) please indicate', 
    'B. Incase the remittance is on the account of business income, please indicate', 
    'C. Incase the remittance is on the account of capital gains, please indicate', 
    'D. Incase if the remittance is not covered by sub-items A, B, and C'
];

const computationFields = [
    'is_system_calculation_applicable', 
    'expenses_reimbursement_frgn', 
    'is_grossed_up', 
    'is_it_taxable', 
    'it_rate', 
    'is_dtaa_taxable', 
    'dtaa_rate'
];

const replicateFields = [
    'template_remitter', 
    'template_remittee', 
    'template_accountant', 
    'is_grossed_up', 
    'is_it_taxable', 
    'it_relevant_section_act', 
    'it_rate', 
    'it_basis', 
    'it_not_taxable_reason', 
    'is_dtaa_taxable', 
    'dtaa_act', 
    'dtaa_article', 
    'dtaa_taxable_income', 
    'dtaa_tax_liability', 
    'dtaa_section', 
    'dtaa_rate', 
    'dtaa_sect_c_basis', 
    'has_tax_residency_certificate', 
    'tax_residency_certificate_number', 
    'nature_of_payment_as_per_dtaa', 
    'dtaa_sect_article_a', 
    'is_such_remittance_taxable_a', 
    'dtaa_sect_rate_a', 
    'amount_taxable_dtaa_a', 
    'furnish_brief_reasons_dtaa_a', 
    'is_such_remittance_taxable_b', 
    'dtaa_sect_article_b', 
    'amount_taxable_dtaa_b', 
    'dtaa_sect_rate_b', 
    'furnish_brief_reasons_dtaa_b', 
    'is_such_remittance_taxable_d', 
    'dtaa_sect_article_d', 
    'amount_taxable_dtaa_d', 
    'dtaa_sect_rate_d', 
    'furnish_brief_reasons_dtaa_d'
]

const systemCalculationTooltip = 'If system calculation is enabled, then the disabled fields will be filled after the backend performs the computation'


async function computationAPI(data) {
    const response = await API.post('cbs/computation/', data, {
        headers: {
            'Content-Type': 'multipart/form-data',
        }, formSerializer: {
            dots: true
        }
    });
    return response.data;
}


async function cbAPI(data) {
    const response = await API.post('/cbs/', data, {
        headers: {
            'Content-Type': 'multipart/form-data',
        }, formSerializer: {
            dots: true
        }
    });
    return response.data;
}


async function cbUpdateAPI(id, data) {
    const response = await API.patch(`cbs/${id}/`, data);
    return response.data;
}

// Answering "Yes" to "is such remittance taxable as per DTAA?" means the section's
// article, amount and rate are the DTAA figures already entered above it. They used
// to be typed again by hand, which is how one transaction ended up with the
// after-tax amount (65116.8) where the taxable income (72352) belonged - and that
// figure then flowed into the government file.
function fillSectionFromDtaa(formik, suffix) {
    return (event) => {
        formik.handleChange(event);
        if (event.target.value !== 'Y') return;
        const copy = (target, source) => {
            const value = formik.values[source];
            formik.setFieldValue(target, value === undefined || value === null ? '' : value);
        };
        copy(`dtaa_sect_article_${suffix}`, 'dtaa_article');
        copy(`amount_taxable_dtaa_${suffix}`, 'dtaa_taxable_income');
        copy(`dtaa_sect_rate_${suffix}`, 'dtaa_rate');
    };
}

const SECTION_SUFFIX = {1: 'a', 2: 'b', 4: 'd'};

/**
 * Mirror the DTAA figures into the chosen section's boxes.
 *
 * Answering Yes copies them (see fillSectionFromDtaa), but the taxable income is
 * usually still blank at that moment - the system calculation fills it afterwards,
 * and a manually entered one is typed later too. Without this, "Amount taxable in
 * India as per DTAA" stayed empty and blocked the step with a required-field error.
 *
 * The amount follows its source, since the calculation is authoritative and BGSS
 * asked for the two to agree. Article and rate are only filled when still blank, so
 * a section-specific article chosen by hand is left alone.
 */
function mirrorSectionFromDtaa(values) {
    const suffix = SECTION_SUFFIX[parseInt(values.dtaa_section)];
    if (!suffix || values[`is_such_remittance_taxable_${suffix}`] !== 'Y') return values;

    const merged = {...values};
    const filled = value => value !== undefined && value !== null && value !== '';

    if (filled(values.dtaa_taxable_income)) {
        merged[`amount_taxable_dtaa_${suffix}`] = values.dtaa_taxable_income;
    }
    if (!filled(merged[`dtaa_sect_rate_${suffix}`]) && filled(values.dtaa_rate)) {
        merged[`dtaa_sect_rate_${suffix}`] = values.dtaa_rate;
    }
    if (!filled(merged[`dtaa_sect_article_${suffix}`]) && filled(values.dtaa_article)) {
        merged[`dtaa_sect_article_${suffix}`] = values.dtaa_article;
    }
    return merged;
}

function invoiceTotal(cbState) {
    const invoices = cbState.formData.invoices?.invoices || [];
    return invoices.reduce((sum, invoice) => sum + (Number(invoice.invoice_amount) || 0), 0);
}

function remittanceCurrency(cbState) {
    const code = cbState.formData.remittee?.currency_of_remittance;
    return code === '99'
        ? cbState.formData.remittee?.currency_of_remittance_other
        : Mapping.remittee.currencySecbCode[code];
}

function FigureWarnings({formik}) {
    const {cbState} = useContext(CBContext);
    const warnings = getFigureWarnings(formik.values, invoiceTotal(cbState));
    if (!warnings.length) return null;

    return (
        // Kept clear of the Cancel / Update / Get Computation buttons, which are
        // absolutely positioned at the top right of this card - same reason the
        // error alerts above are half width.
        <Box sx={{my: 2, width: {xs: '100%', md: '65%'}}}>
            <Alert severity={'warning'} variant={'outlined'}>
                <Typography variant={'subtitle1'} sx={{fontWeight: 'bold'}}>
                    {warnings.length === 1
                        ? 'Please check this figure before saving - it goes into the JSON and the Excel as it is'
                        : `Please check these ${warnings.length} figures before saving - they go into the JSON and the Excel as they are`}
                </Typography>
                <Box component={'ol'} sx={{pl: 2.5, mb: 0}}>
                    {warnings.map(warning => (
                        <li key={warning.title}>
                            <Typography variant={'body2'} sx={{fontWeight: 'bold', mt: 1}}>{warning.title}</Typography>
                            <Typography variant={'body2'}>{warning.detail}</Typography>
                        </li>
                    ))}
                </Box>
            </Alert>
        </Box>
    );
}

/**
 * Warn when the reimbursement is the whole invoice amount.
 *
 * The computation deducts reimbursement before anything else, so a reimbursement
 * equal to the invoices makes the taxable remittance, the amount payable and the
 * remittance after TDS all 0 - in the Excel and in the JSON. The box is the first
 * typeable amount after the greyed-out Amount Payable boxes, and BGSS ended up
 * with the invoice amount in it on a transaction where nobody meant to enter a
 * reimbursement. This is a warning, not a block: a remittance that really is
 * wholly a reimbursement is legitimate. More than the invoices is already refused
 * by the server.
 */
function ReimbursementWarning({formik}) {
    const {cbState} = useContext(CBContext);
    const total = invoiceTotal(cbState);
    const reimbursement = Number(formik.values.expenses_reimbursement_frgn) || 0;

    if (total <= 0 || Math.abs(reimbursement - total) >= 0.005) return null;

    const currency = remittanceCurrency(cbState);
    const amount = formatAmount(total);

    return (
        <Grid item xs={12}>
            <Alert severity={'warning'} variant={'outlined'}>
                <Typography variant={'subtitle1'} sx={{fontWeight: 'bold'}}>
                    Expense Reimbursement is the FULL invoice amount ({amount}{currency ? ` ${currency}` : ''})
                </Typography>
                <Typography variant={'body2'} sx={{mt: 1}}>
                    Reimbursement is deducted before tax. With the whole invoice amount entered here, these
                    will all be <b>0</b> in the computation, the Excel and the JSON:
                </Typography>
                <Typography variant={'body2'} component={'ul'} sx={{my: 1}}>
                    <li>Taxable Remittance</li>
                    <li>Amount Payable</li>
                    <li>Remittance after TDS</li>
                </Typography>
                <Typography variant={'body2'}>
                    <b>If this remittance is not a reimbursement of expenses, change this box to 0.</b> Leave it
                    only if the entire amount really is a reimbursement.
                </Typography>
            </Alert>
        </Grid>
    );
}

function DTAAFields(props) {
    const {cbState} = useContext(CBContext)
    const isReading = cbState.baseData.status >= 2;
    const {formik, error} = props;

    const {dtaa_section, dtaa_taxable_income} = formik.values;
    const sectionSuffix = SECTION_SUFFIX[parseInt(dtaa_section)];
    const sectionAnswer = sectionSuffix ? formik.values[`is_such_remittance_taxable_${sectionSuffix}`] : '';
    const sectionAmount = sectionSuffix ? formik.values[`amount_taxable_dtaa_${sectionSuffix}`] : '';
    const setFieldValue = formik.setFieldValue;

    // Keep the section's amount equal to the DTAA taxable income while the answer is
    // Yes, whenever either changes. The copy on answering Yes and the copy when the
    // calculation returns each cover one order of working; this covers the rest -
    // reopening a saved transaction, changing the section, or re-running the
    // calculation - so the box cannot be left empty and block the step.
    //
    // An empty source now empties the amount too, rather than being skipped. The box
    // is not typeable (see the fields below), so leaving a stale figure behind when
    // the source is cleared would strand a value nobody could correct, and send it to
    // the certificate.
    useEffect(() => {
        if (!sectionSuffix || sectionAnswer !== 'Y') return;
        const source = dtaa_taxable_income === undefined || dtaa_taxable_income === null ? '' : dtaa_taxable_income;
        const current = sectionAmount === undefined || sectionAmount === null ? '' : sectionAmount;
        if (String(current) === String(source)) return;
        setFieldValue(`amount_taxable_dtaa_${sectionSuffix}`, source);
    }, [sectionSuffix, sectionAnswer, sectionAmount, dtaa_taxable_income, setFieldValue]);

    switch (parseInt(formik.values.dtaa_section)) {
        case 0:
            return <></>
        case 1:
            return <>
                <Grid item xs={12}>
                    <Typography variant={'h6'}>DTAA Section A</Typography>
                </Grid>

                <Grid item xs={4}>
                    <RadioField
                        row
                        label={'Is such remittance taxable in India as per DTAA?'}
                        name={'is_such_remittance_taxable_a'}
                        required={!isReading}
                        isReading={isReading}
                        onChange={fillSectionFromDtaa(formik, 'a')}
                        apiError={error}
                    />
                </Grid>

                {
                    formik.values.is_such_remittance_taxable_a === 'Y' && (
                        <>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'dtaa_sect_article_a'}
                                    label={'Article of DTAA'}
                                    fillWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    options={[
                                        'ARTICLE 10',
                                        'ARTICLE 11',
                                        'ARTICLE 12',
                                        'ARTICLE 13',
                                        'ARTICLE 7 READ WITH ARTICLE 5'
                                    ]}
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    name={'amount_taxable_dtaa_a'}
                                    label={'Amount taxable in India as per DTAA'}
                                    type={'number'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    // Always mirrors Taxable Income as per DTAA - see the
                                    // effect above - so anything typed here was overwritten
                                    // on the next render. Shown, not editable.
                                    disabled
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    name={'dtaa_sect_rate_a'}
                                    label={'Rate of deduction of tax at source as per DTAA (%)'}
                                    type={'number'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    apiError={error}
                                />
                            </Grid>
                        </>
                    )
                }

                {
                    formik.values.is_such_remittance_taxable_a === 'N' && (
                        <>
                            <Grid item xs={4}>
                                <TextField
                                    name={'furnish_brief_reasons_dtaa_a'}
                                    label={'furnish brief reasons, specifying article of DTAA'}
                                    type={'text'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    apiError={error}
                                />
                            </Grid>
                        </>
                    )
                }
            </>
        case 2:
            return <>
                <Grid item xs={12}>
                    <Typography variant={'h6'}>DTAA Section B</Typography>
                </Grid>
                
                <Grid item xs={4}>
                    <RadioField
                        row
                        label={'Is such remittance taxable in India as per DTAA?'}
                        name={'is_such_remittance_taxable_b'}
                        isReading={isReading}
                        required={!isReading}
                        onChange={fillSectionFromDtaa(formik, 'b')}
                        apiError={error}
                    />
                </Grid>

                {
                    formik.values.is_such_remittance_taxable_b === 'Y' && (
                        <>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'dtaa_sect_article_b'}
                                    label={'Article of DTAA'}
                                    fillWidth
                                    isReading={isReading}
                                    required={!isReading}
                                    options={[
                                        'ARTICLE 10',
                                        'ARTICLE 11',
                                        'ARTICLE 12',
                                        'ARTICLE 13',
                                        'ARTICLE 7 READ WITH ARTICLE 5'
                                    ]}
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    name={'amount_taxable_dtaa_b'}
                                    label={'Amount taxable in India as per DTAA'}
                                    type={'number'}
                                    fullWidth
                                    isReading={isReading}
                                    required={!isReading}
                                    // Mirrored - see amount_taxable_dtaa_a.
                                    disabled
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    name={'dtaa_sect_rate_b'}
                                    label={'Rate of deduction of tax at source as per DTAA (%)'}
                                    type={'number'}
                                    fullWidth
                                    isReading={isReading}
                                    required={!isReading}
                                    apiError={error}
                                />
                            </Grid>
                        </>
                    )    
                }

                {
                    formik.values.is_such_remittance_taxable_b === 'N' && (
                        <>
                            <Grid item xs={4}>
                                <TextField
                                    name={'furnish_brief_reasons_dtaa_b'}
                                    label={'furnish brief reasons, specifying article of DTAA'}
                                    type={'text'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    apiError={error}
                                />
                            </Grid>
                        </>
                    )
                }
            </>
        case 3:
            return <>
                <Grid item xs={12}>
                    <Typography variant={'h6'}>DTAA Section C</Typography>
                </Grid>
                <Grid item xs={4}>
                    <RadioField
                        row
                        label={'Is such remittance taxable in India as per DTAA?'}
                        name={'is_such_remittance_taxable_c'}
                        isReading={isReading}
                        required={!isReading}
                        apiError={error}
                    />
                </Grid>
                {
                    formik.values.is_such_remittance_taxable_c === 'Y' && (
                        <>
                <Grid item xs={4}>
                    <TextField
                        name={'dtaa_sect_c_amt_long_term'}
                        label={'Amount of long term capital gains'}
                        type={'number'}
                        fullWidth
                        isReading={isReading}
                        required={!isReading}
                        apiError={error}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        type={'number'}
                        name={'dtaa_sect_c_amt_short_term'}
                        label={'Amount of short term capital gains'}
                        fullWidth
                        isReading={isReading}
                        required={!isReading}
                        apiError={error}
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        name={'dtaa_sect_c_basis'}
                        label={'Basis of arriving at taxable income'}
                        fullWidth
                        isReading={isReading}
                        required={!isReading}
                        apiError={error}
                    />
                </Grid>
                        </>
                    )
                }
                {
                    formik.values.is_such_remittance_taxable_c === 'N' && (
                        <Grid item xs={4}>
                            <TextField
                                name={'furnish_brief_reasons_dtaa_c'}
                                label={'furnish brief reasons, specifying article of DTAA'}
                                type={'text'}
                                fullWidth
                                required={!isReading}
                                isReading={isReading}
                                apiError={error}
                            />
                        </Grid>
                    )
                }
            </>
        case 4:
            return <>
                <Grid item xs={12}>
                    <Typography variant={'h6'}>DTAA Section D</Typography>
                </Grid>
                <Grid item xs={4}>
                    <RadioField
                        row
                        label={'Is such remittance taxable in India as per DTAA?'}
                        name={'is_such_remittance_taxable_d'}
                        isReading={isReading}
                        required={!isReading}
                        onChange={fillSectionFromDtaa(formik, 'd')}
                        apiError={error}
                    />
                </Grid>

                {
                    formik.values.is_such_remittance_taxable_d === 'Y' && (
                        <>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'dtaa_sect_article_d'}
                                    label={'Article of DTAA'}
                                    fillWidth
                                    isReading={isReading}
                                    required={!isReading}
                                    options={[
                                        'ARTICLE 10',
                                        'ARTICLE 11',
                                        'ARTICLE 12',
                                        'ARTICLE 13',
                                        'ARTICLE 7 READ WITH ARTICLE 5'
                                    ]}
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    name={'amount_taxable_dtaa_d'}
                                    label={'Amount taxable in India as per DTAA'}
                                    type={'number'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    // Mirrored - see amount_taxable_dtaa_a.
                                    disabled
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <TextField
                                    name={'dtaa_sect_rate_d'}
                                    label={'Rate of deduction of tax at source as per DTAA (%)'}
                                    type={'number'}
                                    fullWidth
                                    isReading={isReading}
                                    required={!isReading}
                                    apiError={error}
                                />
                            </Grid>
                        </>
                    )
                }

                {
                    formik.values.is_such_remittance_taxable_d === 'N' && (
                        <>
                            <Grid item xs={4}>
                                <TextField
                                    name={'furnish_brief_reasons_dtaa_d'}
                                    label={'furnish brief reasons, specifying article of DTAA'}
                                    type={'text'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    apiError={error}
                                />
                            </Grid>
                        </>
                    )
                }
            </>
        default:
            return <></>
    }

}

function FindErrorTab(props) {
    const {error} = props;
    const responseError = []
    if (error.response?.data) {
        const data = error.response.data;

        if (Object.keys(data).includes('remitter')) {
            responseError.push('Error in Remitter');
        }
        if (Object.keys(data).includes('remittee')) {
            responseError.push('Error in Remittee');
        }
        if (Object.keys(data).includes('accountant')) {
            responseError.push('Error in Accountant');
        }
        if (Object.keys(data).includes('invoices')) {
            responseError.push('Error in invoices')
        }
        if (responseError.length > 0) {
            return <>
                <Alert severity={'error'} sx={{my: 1, width: '50%'}}>
                    <List disablePadding>
                        {responseError.map((err, index) => (<ListItem key={index} disablePadding>
                            {err}
                        </ListItem>))}
                    </List>
                </Alert>
            </>
        }
        return <></>
    }
    return <></>
}

export default function RemittanceForm() {

    const {cbState, cbDispatch} = useContext(CBContext)
    const isReading = cbState.baseData.status >= 2;
    const {handleProgressOpen, handleProgressClose} = useContext(ProgressContext);
    const [computationError, setComputationError] = useState(false);
    const [computationModal, setComputationModal] = useState(false);
    const remittanceRef = cbState.baseData.remittanceRef;
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const computation = useMutation(['cbs', 'computation'], computationAPI, {
        onMutate: () => handleProgressOpen(), onSettled: () => handleProgressClose(), onSuccess: (data) => {
            setComputationModal(true);
            cbDispatch({
                type: 'addFormData',
                data: {remittance: mirrorSectionFromDtaa({...remittanceRef.current.values, ...data.cb})}
            });
        }
    });

    const cbCreate = useMutation(['cbs'], cbAPI, {
        onMutate: variables => {
            for (let key in variables) {
                if (variables[key] === '') {
                    variables[key] = null
                }
            }
            handleProgressOpen()
            return variables
        }, onSettled: () => handleProgressClose(), onSuccess: () => {
            navigate(-1)
        }
    })

    const cbUpdate = useMutation(['cbs', cbState.baseData.transactionId], (data) => cbUpdateAPI(cbState.baseData.transactionId, data), {
        onMutate: variables => {
            variables = makeNulls(variables);
            handleProgressOpen()
            return variables
        }, onSettled: () => {
            handleProgressClose()
        }, onSuccess: () => {
            queryClient.invalidateQueries(['cbs', cbState.baseData.transactionId])
        }
    })

    const {
        data: cbData,
        isFetching: cbIsFetching,
        refetch: cbRefetch
    } = useQuery(['cbs', cbState.baseData.transactionId], () => generalAPI(`cbs/${cbState.baseData.transactionId}/`), {
        refetchOnWindowFocus: false,
        retry: false,
        enabled: (cbState.baseData.status !== 0 || cbState.baseData.cloning === true) && !cbState.baseData.pages[cbState.baseData.activeStep].visited,
        select: data => {
            let finalData = {...formData.remittanceFunc()};
            if (cbState.baseData.cloning) {
                for (let key of replicateFields) {
                    finalData[key] = data[key];
                }
            } else {
                finalData = removeNulls(data);
            }
            return finalData;
        }
    });

    useEffect(() => {
        if (!cbState.baseData.pages[cbState.baseData.activeStep].visited && cbData && (cbState.baseData.status !== 0 || cbState.baseData.cloning)) {
            cbDispatch({
                type: 'addUpdateFormData', data: {'remittance': cbData}
            })
        }

    }, [cbData, cbState.baseData.status, cbState.baseData.activeStep, cbState.baseData.pages, cbState.baseData.cloning, cbDispatch]);


    function handleComputationValidation() {
        let values = {...remittanceRef.current.values};
        const setFieldTouched = remittanceRef.current.setFieldTouched;
        const isValid = computationValidationSchema.isValidSync(values);
        if (!isValid) {
            setComputationError(true)
            for (let field of computationFields) {
                setFieldTouched(field, true, true)
            }
        } else {
            values['remitter'] = cbState.formData.remitter;
            values['remittee'] = cbState.formData.remittee;
            values['invoices'] = cbState.formData.invoices.invoices;
            computation.mutate(values)
        }
    }

    function handleSaveRemittance(func) {
        cbDispatch({
            type: 'addFormData', data: {
                remittance: remittanceRef.current.values
            }
        });
        func();
    }

    async function handleUpdateRemittance() {
        let remittance = remittanceRef.current.validateForm();

        const formik = remittanceRef.current;

        const errors = await formik.validateForm();

        console.log("VALUES Update", formik.values);
        console.log("ERRORS Update", errors);

        remittance.then(res => {
            if (Object.keys(res).length === 0) {
                const patchData = getPatchValues(cbState.updateData.remittance, remittanceRef.current.values);
                cbUpdate.mutate(patchData, {
                    onSuccess: () => cbRefetch(),
                })
            } else {
                for (let key of Object.keys(res)) {
                    remittanceRef.current.setFieldTouched(key, true, true);
                }
            }
        })
    }

    const isError = cbCreate.isError || cbUpdate.isError || computation.isError
    const error = cbCreate.error || cbUpdate.error || computation.error

    return (<>
        <Paper sx={{p: 3}}>
            <Formik
                innerRef={remittanceRef}
                initialValues={cbState.formData.remittance}
                validationSchema={remittanceValidationSchema}
                validateOnChange={false}
                enableReinitialize={true}
                onSubmit={(values) => {
                    if (cbState.baseData.status === 0 && !cbState.baseData.cloning) {
                        values['template_remitter'] = cbState.baseData.remitterId;
                        values['template_remittee'] = cbState.baseData.remitteeId;
                        values['template_accountant'] = cbState.baseData.accountantId;
                    }
                    values['remitter'] = cbState.formData.remitter;
                    values['remittee'] = cbState.formData.remittee;
                    values['accountant'] = cbState.formData.accountant;
                    values['invoices'] = cbState.formData.invoices.invoices;
                    cbCreate.mutate(values);
                }}
            >
                {formik => (<>
                    <Box sx={{position: 'relative'}}>
                        <Box sx={{position: 'absolute', right: '0px'}}>
                            {cbState.baseData.status === 1 && <>
                                <Button startIcon={<CancelIcon/>}
                                        onClick={() => navigate(-1) }>Cancel</Button>
                                <Button sx={{ml: 3}} startIcon={<SaveIcon/>}
                                        onClick={handleUpdateRemittance}>Update</Button>
                            </>}
                        </Box>
                        <Box sx={{position: 'absolute', right: '0px', top: '56px'}}>
                            {(formik.values.is_system_calculation_applicable === 'Y') && <>
                                <Tooltip title={'System performs computation, fills the disabled fields'}>
                                    <Button disabled={cbState.baseData.status >= 2} sx={{ml: 3}} onClick={handleComputationValidation}
                                            startIcon={<FunctionsIcon/>} variant={'contained'}>
                                        Get Computation
                                    </Button>
                                </Tooltip>
                            </>}
                        </Box>

                        <Box
                            sx={{
                                position: 'absolute', top: '0px', left: '200px'
                            }}
                        >
                            {cbIsFetching && <CircularProgress size={'2rem'}/>}
                        </Box>
                    </Box>
                    <Box>
                        {computationError && <Alert sx={{width: '50%', my: 2}} severity={'error'}
                                                    onClose={() => setComputationError(false)} icon={false}>
                            Fill all the fields required for computation
                        </Alert>}
                    </Box>
                    {isError && <Box sx={{my: 2, width: '50%'}}>
                        <APIError error={error}/>
                    </Box>}
                    <FigureWarnings formik={formik}/>
                    {
                        cbCreate.isError && <Box>
                            <FindErrorTab error={cbCreate.error}/>
                        </Box>
                    }
                    <Grid container columnSpacing={4} rowSpacing={4}>
                        <Grid item xs={12} mt={-4}>
                            <Typography variant={'h6'}>
                                Remittance Details
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <RadioField
                                row
                                name={'is_system_calculation_applicable'}
                                label={<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    Is System CalculationForm Applicable?
                                    <Tooltip placement={'right'} title={systemCalculationTooltip}>
                                        <HelpIcon/>
                                    </Tooltip>
                                </Box>}
                                apiError={error}
                            />
                        </Grid>
                        {formik.values.is_system_calculation_applicable === 'Y' && <>
                            <Grid item xs={12}>
                                <RadioField
                                    required={!isReading}
                                    isReading={isReading}
                                    name={'rounding'}
                                    label={'Rounding Schema'}
                                    options={[[0, 'No Rounding: (10.05) -> (10), (11.50) -> (12)'], [2, 'Rounding upto 2 decimal: (10.005) -> (10.01), (11.444) -> (11.44)'],]}
                                    apiError={error}
                                />
                            </Grid>
                        </>}
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type={'number'}
                                disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                label={'Amount Payable (Foreign Currency)'}
                                name={'amt_payable_frgn'}
                                apiError={error}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type={'number'}
                                disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                label={'Amount Payable (Indian Currency)'}
                                name={'amt_payable_inr'}
                                apiError={error}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type={'number'}
                                disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                label={'Exchange Rate'}
                                name={'exchange_rate'}
                                apiError={error}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type={'number'}
                                required={!isReading}
                                isReading={isReading}
                                label={'Expense Reimbursement (Foreign Currency)'}
                                name={'expenses_reimbursement_frgn'}
                                apiError={error}
                            />
                        </Grid>
                        <ReimbursementWarning formik={formik}/>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                required={!isReading}
                                type={'date'}
                                disabled={isReading}
                                label={'Proposed Date of Remittance'}
                                name={'proposed_date_of_remittance'}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                apiError={error}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                required={!isReading}
                                type={'date'}
                                disabled={isReading}
                                label={'Mail Received Time'}
                                name={'mail_received'}
                                InputLabelProps={{
                                    shrink: true
                                }}
                                apiError={error}
                            />
                        </Grid>
                        {/* The Certification IP Address box used to sit here. It moved to
                            the Details of Accountant step, which is the section it belongs
                            to on the certificate and where the utility shows it. Nothing
                            replaces it here - it is not a remittance detail. */}
                        <Grid item xs={12}>
                            <RadioField
                                row
                                required={!isReading}
                                isReading={isReading}
                                name={'is_grossed_up'}
                                label={'Whether tax payable has been grossed up as per section 393(10) of the Income Tax Act, 2025'}
                                apiError={error}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant={'h6'}>
                                Taxability under the provision of Income Tax Act, 2025 (without considering DTAA)
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <RadioField
                                row
                                required={!isReading}
                                isReading={isReading}
                                name={'is_it_taxable'}
                                label={'is remittance chargeable to tax in India'}
                                apiError={error}
                            />
                        </Grid>
                        {(formik.values.is_it_taxable === 'Y') ? <>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'it_relevant_section_act'}
                                    label={'Section under which remittance is chargeable to tax'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    options={['9(1)(I)', '194LBB', 'SECTION 115TCA', '9(1)(V)', '9(1)(VI)', '9(1)(VII)']}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'it_basis'}
                                    label={'Basis of determining taxable income and tax liability'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    options={['SECTION 115A OF THE ACT', '9(1)(I)']}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'it_taxable_income'}
                                    label={'Amount of income chargeable to tax'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    type={'number'}
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'it_tax_liability'}
                                    label={'Tax Liability'}
                                    fullWidth
                                    required={!isReading}
                                    isReading={isReading}
                                    type={'number'}
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                {
                                    (formik.values.is_it_taxable === 'Y') && (
                                        <AutoCompleteField
                                            name={'it_rate'}
                                            label={'Rate of deduction of tax at source (%)'}
                                            type={'number'}
                                            fullWidth
                                            required={!isReading}
                                            isReading={isReading}
                                            disabled={isReading}
                                            options={['20.8', '21.84', '23.92', '43.68']}
                                            apiError={error}
                                        />
                                    )
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant={'h6'}>
                                    If income is chargeable to tax in India and any relief is
                                    claimed under DTAA
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <RadioField
                                    row
                                    required={!isReading}
                                    isReading={isReading}
                                    name={'is_dtaa_taxable'}
                                    label={'Is Taxable as per DTAA? (Please indicate whether tax residency certificate is obtained from the recipient of remittance)'}
                                    apiError={error}
                                />
                            </Grid>

                            <Grid item xs={4}>
                                <RadioField
                                    row
                                    required={!isReading}
                                    isReading={isReading}
                                    name={'has_tax_residency_certificate'}
                                    label={'Has Tax Residency Certificate is obtained from the remittee'}
                                    apiError={error}
                                />
                            </Grid>

                            {
                                formik.values.has_tax_residency_certificate === 'Y' && (
                                    <Grid item xs={4}>
                                        <TextField
                                            required={!isReading}
                                            isReading={isReading}
                                            name={'tax_residency_certificate_number'}
                                            label={'Tax Residency Certificate number'}
                                            fullWidth
                                            type={'text'}
                                            apiError={error}
                                        />
                                    </Grid>
                                )
                            }
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'dtaa_act'}
                                    label={'relevant DTAA'}
                                    fullWidth
                                    isReading={isReading}
                                    options={[
                                        'INDIA -  DTAA', 'INDIA - AFGHANISTAN DTAA', 'INDIA - ALBANIYA DTAA',
                                        'INDIA - ARGENTINE DTAA', 'INDIA - ARMENIA DTAA', 'INDIA - AUSTRALIA DTAA',
                                        'INDIA - AUSTRIA DTAA', 'INDIA - BAHAMAS DTAA', 'INDIA - BAHRAIN DTAA',
                                        'INDIA - BANGLADESH DTAA', 'INDIA - BELARUS DTAA', 'INDIA - BELGIUM DTAA',
                                        'INDIA - BELIZE DTAA', 'INDIA - BERMUDA DTAA', 'INDIA - BHUTAN DTAA',
                                        'INDIA - BOTSWANA DTAA', 'INDIA - BRAZIL DTAA',
                                        'INDIA - BRITISH VIRGIN ISLANDS DTAA', 'INDIA - BRUNEI DARUSSALAM DTAA',
                                        'INDIA - BULGARIA DTAA', 'INDIA - CANADA DTAA', 'INDIA - CAYMAN ISLANDS DTAA',
                                        'INDIA - CHILE DTAA', 'INDIA - CHINA DTAA', 'INDIA - COLOMBIA DTAA',
                                        'INDIA - CROATIA DTAA', 'INDIA - CYPRUS DTAA',
                                        'INDIA - CZECH REPUBLIC DTAA', 'INDIA - DENMARK DTAA', 'INDIA - ESTONIA DTAA',
                                        'INDIA - ETHIOPIA DTAA', 'INDIA - FIJI DTAA', 'INDIA - FINLAND DTAA',
                                        'INDIA - FRANCE DTAA', 'INDIA - GEORGIA DTAA', 'INDIA - GERMANY DTAA',
                                        'INDIA - GIBRALTAR DTAA', 'INDIA - GREECE DTAA', 'INDIA - GUERNSEY DTAA',
                                        'INDIA - HASHEMITE KINGDOM OF JORDAN DTAA', 'INDIA - HONG KONG DTAA',
                                        'INDIA - HUNGARY DTAA', 'INDIA - ICELAND DTAA', 'INDIA - INDONESIA DTAA',
                                        'INDIA - IRAN DTAA', 'INDIA - IRELAND DTAA', 'INDIA - ISLE OF MAN DTAA',
                                        'INDIA - ISRALE DTAA', 'INDIA - ITALY DTAA', 'INDIA - JAPAN DTAA',
                                        'INDIA - JERSEY DTAA', 'INDIA - KAZAKHSTAN DTAA', 'INDIA - KENYA DTAA',
                                        'INDIA - KOREA DTAA', 'INDIA - KUWAIT DTAA', 'INDIA - KYRGYZ REPUBLIC DTAA',
                                        'INDIA - LATVIA DTAA', 'INDIA - LEBANON DTAA', 'INDIA - LIBERIA DTAA',
                                        'INDIA - LIBYA DTAA', 'INDIA - LITHUANIA DTAA', 'INDIA - LUXEMBOURG DTAA',
                                        'INDIA - MACAO SAR DTAA', 'INDIA - MACEDONIA DTAA', 'INDIA - MALAYSIA DTAA',
                                        'INDIA - MALDIVES DTAA', 'INDIA - MALTA DTAA', 'INDIA - MARSHALL ISLAND DTAA',
                                        'INDIA - MAURITIUS DTAA', 'INDIA - MONGOLIA DTAA', 'INDIA - MONTENEGRO DTAA',
                                        'INDIA - MOROCCO DTAA', 'INDIA - MOZAMBIQUE DTAA',
                                        'INDIA - MYANMAR (UNION OF MYANMAR) DTAA', 'INDIA - NAMIBIA DTAA',
                                        'INDIA - NEPAL DTAA', 'INDIA - NETHERLANDS DTAA', 'INDIA - NEW ZEALAND DTAA',
                                        'INDIA - NORWAY DTAA', 'INDIA - OMAN DTAA',
                                        'INDIA - ORIENTAL REPUBLIC OF URUGUAY DTAA', 'INDIA - PAKISTAN DTAA',
                                        'INDIA - PEOPLE DEMOCRATIC REPUBLIC OF YEMEN DTAA', 'INDIA - PHILIPPINES DTAA',
                                        'INDIA - POLAND DTAA', 'INDIA - PORTUGUESE REPUBLIC DTAA', 'INDIA - QATAR DTAA',
                                        'INDIA - ROMANIA DTAA', 'INDIA - RUSSIA DTAA',
                                        'INDIA - SAINT KITTS AND NEVIS DTAA',
                                        'INDIA - SAINT VINCENT AND THE GRENADINES DTAA', 'INDIA - SAN MARINO DTAA',
                                        'INDIA - SAUDI ARABAI DTAA', 'INDIA - SERBIA DTAA', 'INDIA - SEYCHELLES DTAA',
                                        'INDIA - SINGAPORE DTAA', 'INDIA - SLOVAK REPUBLIC DTAA',
                                        'INDIA - SLOVENIA DTAA', 'INDIA - SOUTH AFRICA DTAA', 'INDIA - SPAIN DTAA',
                                        'INDIA - SRI LANKA DTAA', 'INDIA - SUDAN DTAA', 'INDIA - SWEDEN DTAA',
                                        'INDIA - SWISS CONFEDERATION DTAA', 'INDIA - SYRIAN ARAB REPUBLIC DTAA',
                                        'INDIA - TAIPEI DTAA', 'INDIA - TAJIKISTAN DTAA', 'INDIA - TANZANIA DTAA',
                                        'INDIA - THAILAND DTAA', 'INDIA - TRINIDAD AND TOBAGO DTAA',
                                        'INDIA - TURKEY DTAA', 'INDIA - TURKMENISTAN DTAA', 'INDIA - UAE DTAA',
                                        'INDIA - UAR (EGYPT) DTAA', 'INDIA - UGANDA DTAA', 'INDIA - UK DTAA',
                                        'INDIA - UKRAINE DTAA', 'INDIA - UNITED MEXICAN STATES DTAA',
                                        'INDIA - USA DTAA', 'INDIA - UZBEKISTAN DTAA', 'INDIA - VIETNAM DTAA',
                                        'INDIA - YEMEN ARAB REPUBLIC DTAA', 'INDIA - ZAMBIA DTAA'
                                    ]}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'dtaa_article'}
                                    label={'article of DTAA'}
                                    fullWidth
                                    isReading={isReading}
                                    options={[
                                        'ARTICLE 10',
                                        'ARTICLE 11',
                                        'ARTICLE 12',
                                        'ARTICLE 13',
                                        'ARTICLE 7 READ WITH ARTICLE 5'
                                    ]}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'nature_of_payment_as_per_dtaa'}
                                    label={'nature of payment as per DTAA '}
                                    fullWidth
                                    isReading={isReading}
                                    type={'text'}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'dtaa_taxable_income'}
                                    label={'Taxable Income as per DTAA'}
                                    fullWidth
                                    type={'number'}
                                    required={!isReading}
                                    isReading={isReading}
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    // Typed by hand when the system calculation is off; carry it
                                    // into the chosen section's amount the same way the
                                    // calculation does.
                                    afterBlur={value => formik.setValues(
                                        mirrorSectionFromDtaa({...formik.values, dtaa_taxable_income: value})
                                    )}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'dtaa_tax_liability'}
                                    label={'Tax Liability as per DTAA'}
                                    fullWidth
                                    type={'number'}
                                    required={!isReading}
                                    isReading={isReading}
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                {
                                    (formik.values.is_dtaa_taxable === 'Y') && (formik.values.is_system_calculation_applicable === 'Y') && (
                                        <TextField
                                            name={'dtaa_rate'}
                                            label={'DTAA Rate'}
                                            type={'number'}
                                            fullWidth
                                            required={!isReading}
                                            isReading={isReading}
                                            disabled={isReading || !(formik.values.is_system_calculation_applicable === 'Y')}
                                            apiError={error}
                                        />
                                    )
                                }
                            </Grid>
                            <Collapse
                                component={Grid} item xs={12}
                                in={(formik.values.is_dtaa_taxable === 'Y')}>
                                <RadioField
                                    required={!isReading}
                                    isReading={isReading}
                                    label={'DTAA Section'}
                                    name={'dtaa_section'}
                                    options={[[1, dtaaSections[0], {disabled: isReading}], [2, dtaaSections[1], {disabled: isReading}], [3, dtaaSections[2], {disabled: isReading || (formik.values.is_system_calculation_applicable === 'Y')}], [4, dtaaSections[3], {disabled: isReading}],]}
                                    apiError={error}
                                />
                            </Collapse>
                            {
                                formik.values.is_dtaa_taxable === 'Y' && (
                                    <DTAAFields formik={formik}/>
                                )
                            }
                        </> : <>
                            <Grid item xs={4}>
                                <AutoCompleteField
                                    name={'it_not_taxable_reason'}
                                    label={'IT ACT not applicable reason'}
                                    fillWidth
                                    isReading={isReading}
                                    required={!isReading}
                                    options={['AMOUNT NOT TAXABLE UNDER THE ACT', 'PAYMENT OF INTEREST FALLS WITHIN THE DEFINITION OF INTEREST U/ S 2(28A) AND HENCE EXEMPT UNDER SECTION 194A']}
                                    apiError={error}
                                />
                            </Grid>
                        </>}
                        {formik.values.is_it_taxable === 'Y' && <>
                            <Grid item xs={12}>
                                <Typography variant={'h6'}>TDS Details</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'tds_amt_frgn'}
                                    label={'TDS Amount (Foreign Currency)'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'tds_amt_inr'}
                                    label={'TDS Amount (Indian Currency)'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'tds_rate'}
                                    label={'TDS Rate'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <RadioField
                                    row
                                    fullWidth
                                    label={'TDS Rate as Per'}
                                    name={'tds_rate_as_per'}
                                    options={Object.entries(Mapping.transactionCB.rateTdsSecbFlg).filter(([key, value]) => key !== '-1').map((entry) => [...entry, {disabled: (formik.values.is_system_calculation_applicable === 'Y')}])}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'actual_amt_after_tds_frgn'}
                                    label={'Actual Amount after TDS (Foreign Currency)'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'actual_amt_after_tds_inr'}
                                    label={'Actual Amount after TDS (India Currency)'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    type={'date'}
                                    disabled={isReading}
                                    label={'Date of Deduction of tax at source (if any)'}
                                    name={'date_of_deduction'}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    apiError={error}
                                />
                            </Grid>
                        </>}
                        {/* Not taxable: no tax is deducted, so the amount after TDS is the amount
                            payable. These two boxes were only shown under TDS Details, so with the
                            answer at No a wrong figure here could not be seen or corrected without
                            switching to Yes and back. */}
                        {formik.values.is_it_taxable === 'N' && <>
                            <Grid item xs={12}>
                                <Typography variant={'h6'}>Remittance after TDS</Typography>
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'actual_amt_after_tds_frgn'}
                                    label={'Actual Amount after TDS (Foreign Currency)'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    name={'actual_amt_after_tds_inr'}
                                    label={'Actual Amount after TDS (India Currency)'}
                                    type={'number'}
                                    fullWidth
                                    disabled={isReading || (formik.values.is_system_calculation_applicable === 'Y')}
                                    apiError={error}
                                />
                            </Grid>
                        </>}
                    </Grid>
                </>)}
            </Formik>
        </Paper>

        <ComputationModal open={computationModal} setOpen={setComputationModal} computation={computation}/>

        <Box sx={{textAlign: 'center', mt: 3}}>
            <Button
                startIcon={<NavigateBeforeIcon/>}
                variant={'outlined'}
                onClick={() => handleSaveRemittance(() => cbState.baseData.setActiveStep(1, cbState.baseData.activeStep))}
            >
                Previous
            </Button>
            <Button
                startIcon={<NavigateNextIcon/>}
                variant={'contained'}
                sx={{ml: 3}}
                disabled={cbState.baseData.status <= 1}
                onClick={() => cbState.baseData.setActiveStep(3, cbState.baseData.activeStep)}
            >
                Next
            </Button>
        </Box>
    </>)
}